"use client";

import { Box, colors, Heading, HStack, List, ListItem, Text } from "@biblioteksentralen/react";
import { isNonNil } from "@libry-content/common";
import { isLocalizedField, TranslateSanityContent } from "@libry-content/localization";
import { ReactNode } from "react";
import { ChevronRight } from "react-feather";
import { DefaultContainer } from "../../../../../components/ContentContainer";
import InternalLink from "../../../../../components/InternalLink";
import { useHeaderNavigationLinks } from "../../../../../components/layout/header/useHeaderNavigationLinks";
import { TranslateMicroText, useTranslation } from "../../../../../utils/hooks/useTranslation";
import { Location } from "../../../../../utils/sitemap";
import { DefaultPageLayout } from "../../../../../components/layout/DefaultPageLayout";

type Condition = (location: Location) => boolean;

const getSpecialFooterNavigationLinks = (t: TranslateMicroText): { condition: Condition; title: string }[] => [
  { title: t("Ansatte"), condition: ({ path }) => path === "/ansatte" },
  { title: t("Personvernerklæring"), condition: ({ path }) => path === "/personvern" },
  {
    title: t("Våre bibliotek"),
    condition: ({ path, subLocations }) => path === "/bibliotek" && subLocations.length > 1,
  },
  {
    title: t("Om biblioteket"),
    condition: ({ path, subLocations }) => path === "/bibliotek" && subLocations?.length === 1,
  },
];

const useGetTitle = () => {
  const headerNavigationLinks = useHeaderNavigationLinks();

  return (t: TranslateMicroText, ts: TranslateSanityContent, location: Location): string => {
    const headerNavigationPath = headerNavigationLinks.find((link) => link.path == location.path);
    if (headerNavigationPath) return headerNavigationPath.title;

    const specialFooterNavigationLinks = getSpecialFooterNavigationLinks(t);
    const specialFooterNavigationPath = specialFooterNavigationLinks.find(({ condition }) => condition(location));
    if (specialFooterNavigationPath) return specialFooterNavigationPath.title;

    const title = isLocalizedField(location.title) ? ts(location.title) : location.title;
    if (title) return title;

    if (location.path === "") return t("forside");

    return location.path?.replace(/^\//, "") ?? "ukjent side";
  };
};

const SiteMapList = ({ children }: { children: ReactNode }) => {
  return (
    <List marginTop="0.5rem" marginBottom="0.5rem" borderLeft="1px solid white" marginLeft="-1px">
      {children}
    </List>
  );
};

const SiteMapListItem = ({ location, level = 0 }: { location: Location; level?: number }) => {
  const { t, ts } = useTranslation();
  const hasSubLocations = !!location.subLocations?.length;
  const isRootLevel = level === 0;
  const getTitle = useGetTitle();

  return (
    <ListItem
      key={location.path || "/"}
      paddingTop="0.5rem"
      paddingBottom={!isRootLevel || hasSubLocations ? "0" : "0.5rem"}
      marginLeft={`${level}rem`}
      borderLeft={!isRootLevel ? `1px solid ${colors.grey30}` : "none"}
    >
      <HStack alignItems="flex-start">
        {!isRootLevel && (
          <>
            <Text color={colors.grey80} marginLeft="0.25rem">
              {"–"}{" "}
            </Text>
            <Box position="relative" top="0.32rem" left="-0.5rem">
              <ChevronRight color={colors.grey80} size="1em" />
            </Box>
          </>
        )}
        <InternalLink
          href={location.path || "/"}
          css={{ ":first-letter": { textTransform: "capitalize" } }}
          textDecoration="none"
          _hover={{ textDecoration: "underline" }}
        >
          {getTitle(t, ts, location)}
          {/* eslint-disable-next-line i18next/no-literal-string */}
          {!!location.subtitle && <> ({location.subtitle})</>}
        </InternalLink>
      </HStack>
      {hasSubLocations && (
        <SiteMapList>
          {location.subLocations?.map((location) =>
            isNonNil(location.path) ? (
              <SiteMapListItem key={location.path || "/"} location={location} level={level + 1} />
            ) : null
          )}
        </SiteMapList>
      )}
    </ListItem>
  );
};

export default function SiteMapClientSide({ locations }: { locations: Location[] | undefined }) {
  const { t } = useTranslation();
  const description = t("I dette hierarkiske nettstedkartet finner du liste over sider nettstedet består av.");

  return (
    <DefaultPageLayout>
      <DefaultContainer>
        <Heading as="h1" marginBottom="1rem">
          {t("Nettstedkart")}
        </Heading>
        <Text fontSize="lg" paddingBottom="1rem">
          {description}
        </Text>
        <SiteMapList>
          {locations?.map((location) =>
            isNonNil(location.path) ? <SiteMapListItem key={location.path || "/"} location={location} /> : null
          )}
        </SiteMapList>
      </DefaultContainer>
    </DefaultPageLayout>
  );
}
